import type { FTRVoteDecision, ScreenVoteDecision, Study } from './StudyList';

// a minimal representation of a study for use in study list toolbars and dialogs
export type SimplifiedStudy = {
  id: number;
  title: string | null;
  study_id: string;
  unique_index: number;
};

// Sort keys the study list endpoints accept (Search::BaseStrategy::SORT_OPTIONS)
export type SortValue =
  | 'author'
  | 'title'
  | 'covidence_number'
  | 'most_recent'
  | 'relevancy'
  | 'most_relevant_ai';

export const MOST_RELEVANT_AI_SORT: SortValue = 'most_relevant_ai';

export type StudyListStage = 'screening' | 'fullText' | 'extraction';

export type FilterVote = ScreenVoteDecision | FTRVoteDecision;

export type SearchFields =
  | 'everything'
  | 'title_and_abstract'
  | 'authors_exact'
  | 'authors_fuzzy';

export type FilterArgs = {
  keywords: string[];
  within: SearchFields;
  has_full_text?: boolean;
  vote?: FilterVote;
};

// No filters applied, as the initial data from Rails and the toolbar's Reset
export const NO_FILTER_ARGS: FilterArgs = {
  keywords: [],
  within: 'title_and_abstract',
};

// Sort, display count and filters, sent in a single fetch
export type SortAndFilterArgs = {
  sort: SortValue;
  display: number;
  filter: FilterArgs;
};

// Applies the sort, display and filters, and resolves with the number of studies found
export type SortAndFilter = (args: SortAndFilterArgs) => Promise<number>;

// One page of any study list, as the hook stores it
export type StudyListPage = {
  studies: Study[];
  totalEntries: number;
  remainingOnServer: number;
};

// The filter param that picks a study list tab, from StudyListPresenter::FILTERS
export type StudyListFilter =
  | 'all'
  | 'comparison_required'
  | 'complete'
  | 'other_vote_required'
  | 'ready_for_extraction'
  | 'votes_in_conflict'
  | 'vote_required_from';

export type ExtractionListFilter =
  | 'complete'
  | 'comparison_required'
  | 'ready_for_extraction';

// The list filters for title and abstract and full text screening
export type ScreeningListFilter = Extract<
  StudyListFilter,
  'vote_required_from' | 'votes_in_conflict' | 'other_vote_required'
>;
